import { computed, reactive, ref, watch, type Ref } from "vue";
import { getCompositionAutocomplete, getReferenceData } from "@/api/shooting";
import { getCategoriesTree } from "@/api/categories";
import { getCouleursOfficielles } from "@/api/couleurs";
import { getVendeurMarques } from "@/api/brands";
import {
  SHOPIFY_BULK_BATCH_SIZE,
  saveShopifyBulkOverridesInBatches,
} from "@/api/shopify";
import {
  parsePackQuantities,
  parsePositivePrice,
  parsePositiveWeight,
  getMelUnitWeightError,
  validateCompositionsForSave,
} from "@/utils/melCompositions";
import { rememberCompositionSet } from "@/utils/melCompositionHistory";
import { brandSelectOptions } from "@/utils/sellerBrand";
import {
  formatDeclinaisonLabel,
  getDeclinaisonLength,
} from "@/utils/declinaisonLabel";
import type { Brand } from "@/types/brand";
import type { PrestashopMelOverrides, PrestashopReferenceData } from "@/types/prestashop";
import type { ShopifyProduct } from "@/types/shopify";
import type {
  CompositionAutocompleteOption,
  CompositionItem,
  CompositionLocalisation,
  CompositionMateriau,
  CompositionType,
  CouleurAPI,
  CouleurSelectorItem,
} from "@/types/mel-components";
import type { CategorieNode, CategoryPathSelection } from "@/types/categorie";

const option = (id: string | number, label: string) => ({
  value: String(id),
  label,
});

function countCsvParts(value: string | number | undefined | null) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean).length;
}

export type ShopifyMelBulkSheetContext = {
  selectedProductIds: Ref<string[]>;
  selectedProductsById: Ref<Map<string, ShopifyProduct>>;
  onReloadCatalog: () => Promise<void>;
  success: (message: string) => void;
  error: (message: string) => void;
  showBulkComplete: Ref<boolean>;
};

export function useShopifyMelBulkSheet(options: ShopifyMelBulkSheetContext) {
  const showBulkComplete = options.showBulkComplete;
  const bulkModalLoading = ref(false);
  const savingBulkComplete = ref(false);
  const bulkProgressLabel = ref("");

  const referenceData = ref<PrestashopReferenceData | null>(null);
  const categoriesTree = ref<CategorieNode[]>([]);
  const officialColors = ref<CouleurAPI[]>([]);
  const sellerBrands = ref<Brand[]>([]);

  const bulkSelectedCategoryLevel1 = ref("");
  const bulkSelectedCategoryLevel2 = ref("");
  const bulkSelectedCategoryLevel3 = ref("");
  const bulkSelectedCompositions = ref<CompositionItem[]>([]);
  const bulkCompositionType = ref<CompositionType>("autres");
  const bulkCompositionLocations = ref<CompositionLocalisation[]>([]);
  const bulkShoeCompositions = ref<CompositionMateriau[]>([]);
  const bulkCompositionAutocompleteOptions = ref<CompositionAutocompleteOption[]>(
    []
  );
  const bulkShowCompositionDropdown = ref(false);
  const bulkIsLoadingCompositionAutocomplete = ref(false);
  const bulkSelectedColors = ref<CouleurSelectorItem[]>([]);

  const bulkForm = reactive({
    brandId: "",
    categoryId: "",
    collectionId: "",
    provenanceId: "",
    price: "",
    weight: "",
    soldBy: "",
    packId: "",
    sizeRangeId: "",
  });

  const soldByOptions = [
    option("tailles", "Tailles aux choix"),
    option("couleurs", "À la couleur"),
    option("melangees", "Couleurs mélangées"),
  ];

  const bulkSelectionProducts = computed(() =>
    options.selectedProductIds.value
      .map((productId) => options.selectedProductsById.value.get(productId))
      .filter((product): product is ShopifyProduct => Boolean(product))
  );

  const bulkBatchCount = computed(() =>
    Math.ceil(bulkSelectionProducts.value.length / SHOPIFY_BULK_BATCH_SIZE)
  );

  const bulkMissingSummary = computed(() => {
    const counts = new Map<string, number>();
    for (const product of bulkSelectionProducts.value) {
      for (const field of product.missingFields ?? []) {
        counts.set(field, (counts.get(field) || 0) + 1);
      }
    }
    return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
  });

  const hasBulkFormValues = computed(
    () =>
      Boolean(bulkForm.brandId) ||
      Boolean(bulkForm.categoryId) ||
      bulkSelectedCompositions.value.length > 0 ||
      bulkSelectedColors.value.length > 0 ||
      Boolean(bulkForm.collectionId) ||
      Boolean(bulkForm.provenanceId) ||
      Boolean(bulkForm.price) ||
      Boolean(bulkForm.weight) ||
      Boolean(bulkForm.soldBy) ||
      Boolean(bulkForm.packId) ||
      Boolean(bulkForm.sizeRangeId)
  );

  const brandOptions = computed(() => brandSelectOptions(sellerBrands.value));

  const collectionOptions = computed(() =>
    (referenceData.value?.collections || []).map((item) =>
      option(item.id, item.label || String(item.id))
    )
  );

  const provenanceOptions = computed(() =>
    (referenceData.value?.provenances || []).map((item) =>
      option(item.id, item.label || String(item.id))
    )
  );

  const sizeRangeOptions = computed(() =>
    (referenceData.value?.declinaisons || []).map((item) =>
      option(
        item.id,
        formatDeclinaisonLabel(item.label, item.tailles) || String(item.id)
      )
    )
  );

  const categoryLevel1Options = computed(() =>
    categoriesTree.value.map((item) =>
      option(item.id_categorie, item.label || String(item.id_categorie))
    )
  );

  const bulkCategoryLevel2Options = computed(() => {
    if (!bulkSelectedCategoryLevel1.value) return [];
    const level1 = categoriesTree.value.find(
      (item) => String(item.id_categorie) === bulkSelectedCategoryLevel1.value
    );
    return (level1?.children || []).map((item) =>
      option(item.id_categorie, item.label || String(item.id_categorie))
    );
  });

  const bulkCategoryLevel3Options = computed(() => {
    if (!bulkSelectedCategoryLevel1.value || !bulkSelectedCategoryLevel2.value) {
      return [];
    }
    const level1 = categoriesTree.value.find(
      (item) => String(item.id_categorie) === bulkSelectedCategoryLevel1.value
    );
    const level2 = level1?.children?.find(
      (item) => String(item.id_categorie) === bulkSelectedCategoryLevel2.value
    );
    return (level2?.children || []).map((item) =>
      option(item.id_categorie, item.label || String(item.id_categorie))
    );
  });

  function packOptionsForSizeRange(sizeRangeId: string) {
    if (!sizeRangeId) return [];
    const selectedDeclinaison = (referenceData.value?.declinaisons || []).find(
      (item) => String(item.id) === String(sizeRangeId)
    );
    const tailleCount = getDeclinaisonLength(selectedDeclinaison?.tailles);
    if (!tailleCount) return [];
    return (referenceData.value?.packs || [])
      .filter((pack) => countCsvParts(pack.value) === tailleCount)
      .map((item) =>
        option(
          item.id,
          item.value
            ? `${item.label || item.id} (${item.value})`
            : item.label || String(item.id)
        )
      );
  }

  const bulkPackOptions = computed(() =>
    packOptionsForSizeRange(bulkForm.sizeRangeId)
  );

  const bulkSelectedSizeNames = computed(() => {
    const range = (referenceData.value?.declinaisons || []).find(
      (item) => String(item.id) === bulkForm.sizeRangeId
    );
    return String(range?.tailles || "")
      .split(",")
      .map((size) => size.trim())
      .filter(Boolean);
  });

  const normalizedBulkCompositionsForSave = computed(() => {
    if (bulkCompositionType.value !== "chaussures") {
      return bulkSelectedCompositions.value.map((composition) => ({
        compositionId: composition.compositionId,
        locationId: composition.localisationId,
        percentage: Number(composition.percentage) || 0,
      }));
    }
    const countByLocation = new Map<number, number>();
    for (const composition of bulkSelectedCompositions.value) {
      countByLocation.set(
        composition.localisationId,
        (countByLocation.get(composition.localisationId) || 0) + 1
      );
    }
    return bulkSelectedCompositions.value.map((composition) => ({
      compositionId: composition.compositionId,
      locationId: composition.localisationId,
      percentage: 100 / (countByLocation.get(composition.localisationId) || 1),
    }));
  });

  const bulkMelangeesPackMismatchMessage = computed(() => {
    if (bulkForm.soldBy !== "melangees" || !bulkForm.packId) return "";
    if (!bulkSelectedColors.value.length) return "";
    const selectedPack = (referenceData.value?.packs || []).find(
      (pack) => String(pack.id) === String(bulkForm.packId)
    );
    const packQuantites = parsePackQuantities(selectedPack?.value);
    if (!packQuantites.length) return "";

    const sommeQuantites = new Array(packQuantites.length).fill(0);
    for (const color of bulkSelectedColors.value) {
      (color.quantitesParTaille || []).forEach((quantity, index) => {
        if (index < sommeQuantites.length) {
          sommeQuantites[index] += Number(quantity) || 0;
        }
      });
    }

    for (let index = 0; index < packQuantites.length; index += 1) {
      if (packQuantites[index] !== sommeQuantites[index]) {
        return `La somme des quantités des couleurs (${sommeQuantites.join(
          "-"
        )}) ne correspond pas aux quantités du paquet (${packQuantites.join(
          "-"
        )})`;
      }
    }
    return "";
  });

  function validateBulkOverridesBeforeSave(): string | null {
    if (bulkForm.price.trim() && !parsePositivePrice(bulkForm.price)) {
      return "Le prix doit être supérieur à 0.";
    }
    if (bulkForm.weight.trim()) {
      const weightError = getMelUnitWeightError(bulkForm.weight);
      if (weightError) return weightError;
    }
    const soldBy = bulkForm.soldBy;
    const hasColors = bulkSelectedColors.value.length > 0;
    const appliesSoldBy =
      soldBy === "couleurs" || soldBy === "melangees" || soldBy === "tailles";

    if (appliesSoldBy && soldBy !== "tailles") {
      if (!bulkForm.sizeRangeId) {
        return "Sélectionnez une déclinaison pour ce mode de vente.";
      }
      if (!bulkForm.packId) {
        return "Sélectionnez un pack pour ce mode de vente.";
      }
    }

    if (soldBy === "melangees" && hasColors) {
      if (!bulkForm.sizeRangeId) {
        return "Sélectionnez une déclinaison pour saisir les quantités mélangées.";
      }
      const missingQuantities = bulkSelectedColors.value.some(
        (color) =>
          !color.quantitesParTaille?.some((quantity) => Number(quantity) > 0)
      );
      if (missingQuantities) {
        return "Renseignez au moins une quantité par couleur pour les couleurs mélangées.";
      }
      if (bulkMelangeesPackMismatchMessage.value) {
        return bulkMelangeesPackMismatchMessage.value;
      }
    }

    return null;
  }

  const bulkSoldByValidationMessage = computed(() => {
    if (!showBulkComplete.value) return "";
    return validateBulkOverridesBeforeSave() || "";
  });

  const bulkCompositionValidationMessage = computed(() => {
    if (!showBulkComplete.value || !bulkSelectedCompositions.value.length) {
      return "";
    }
    const { valid, message } = validateCompositionsForSave(
      normalizedBulkCompositionsForSave.value,
      bulkCompositionType.value,
      bulkCompositionLocations.value
    );
    return valid ? "" : message;
  });

  const bulkFormValidationMessage = computed(
    () =>
      bulkSoldByValidationMessage.value ||
      bulkCompositionValidationMessage.value
  );

  const canSaveBulkForm = computed(
    () => hasBulkFormValues.value && !bulkFormValidationMessage.value
  );

  function configureBulkCompositionReferences(clearSelection = false) {
    const categoryId =
      bulkSelectedCategoryLevel1.value || bulkForm.categoryId;
    const isShoe =
      categoryId.startsWith("14") ||
      ["130106", "130306", "130405"].includes(bulkForm.categoryId);
    bulkCompositionType.value = isShoe ? "chaussures" : "autres";
    const categoryFilter = isShoe ? "14" : null;
    const references = referenceData.value?.compositions || [];
    bulkCompositionLocations.value = references
      .filter(
        (item) =>
          item.type === "localisation" &&
          (item.id_categorie ?? null) === categoryFilter
      )
      .map((item) => ({
        id_composition_localisation: Number(item.id),
        libelle: item.libelle || item.label || String(item.id),
      }));
    bulkShoeCompositions.value = isShoe
      ? references
          .filter(
            (item) =>
              item.type === "composition" &&
              String(item.id_categorie || "") === "14"
          )
          .map((item) => ({
            id_composition: Number(item.id),
            libelle: item.libelle || item.label || String(item.id),
          }))
      : [];
    if (clearSelection) bulkSelectedCompositions.value = [];
  }

  function syncBulkMelangeesColors() {
    if (bulkForm.soldBy !== "melangees") return;
    const sizes = bulkSelectedSizeNames.value;
    if (!sizes.length || !bulkSelectedColors.value.length) return;
    bulkSelectedColors.value = bulkSelectedColors.value.map((color) => ({
      ...color,
      quantitesParTaille: sizes.map(
        (_, index) => Number(color.quantitesParTaille?.[index]) || 0
      ),
    }));
  }

  function syncBulkCategoryIdFromLevels() {
    if (bulkSelectedCategoryLevel3.value) {
      bulkForm.categoryId = bulkSelectedCategoryLevel3.value;
      return;
    }
    if (bulkSelectedCategoryLevel2.value) {
      bulkForm.categoryId = bulkCategoryLevel3Options.value.length
        ? ""
        : bulkSelectedCategoryLevel2.value;
      return;
    }
    if (bulkSelectedCategoryLevel1.value) {
      bulkForm.categoryId = bulkCategoryLevel2Options.value.length
        ? ""
        : bulkSelectedCategoryLevel1.value;
      return;
    }
    bulkForm.categoryId = "";
  }

  function resetBulkForm() {
    bulkForm.brandId = "";
    bulkForm.categoryId = "";
    bulkForm.collectionId = "";
    bulkForm.provenanceId = "";
    bulkForm.price = "";
    bulkForm.weight = "";
    bulkForm.soldBy = "";
    bulkForm.packId = "";
    bulkForm.sizeRangeId = "";
    bulkSelectedCategoryLevel1.value = "";
    bulkSelectedCategoryLevel2.value = "";
    bulkSelectedCategoryLevel3.value = "";
    bulkSelectedCompositions.value = [];
    bulkCompositionAutocompleteOptions.value = [];
    bulkShowCompositionDropdown.value = false;
    bulkSelectedColors.value = [];
    configureBulkCompositionReferences();
  }

  async function preloadBulkReferences() {
    const [references, tree, colors, brands] = await Promise.all([
      getReferenceData(),
      categoriesTree.value.length
        ? Promise.resolve(categoriesTree.value)
        : getCategoriesTree("fr"),
      getCouleursOfficielles(),
      sellerBrands.value.length
        ? Promise.resolve(sellerBrands.value)
        : getVendeurMarques(),
    ]);
    referenceData.value = references;
    categoriesTree.value = tree;
    officialColors.value = colors;
    sellerBrands.value = brands;
  }

  async function openBulkComplete() {
    if (!options.selectedProductIds.value.length) return;
    resetBulkForm();
    showBulkComplete.value = true;
    bulkModalLoading.value = true;
    try {
      await preloadBulkReferences();
      configureBulkCompositionReferences();
    } catch (caught) {
      showBulkComplete.value = false;
      options.error(
        caught instanceof Error
          ? caught.message
          : "Référentiels eFashion indisponibles."
      );
    } finally {
      bulkModalLoading.value = false;
    }
  }

  async function closeBulkComplete() {
    if (bulkModalLoading.value || savingBulkComplete.value) return;
    const wasOpen = showBulkComplete.value;
    showBulkComplete.value = false;
    if (wasOpen) {
      await options.onReloadCatalog();
    }
  }

  async function saveBulkComplete() {
    if (!options.selectedProductIds.value.length || !canSaveBulkForm.value) {
      return;
    }

    const bulkCompositions = bulkSelectedCompositions.value.length
      ? normalizedBulkCompositionsForSave.value
      : [];
    if (bulkSelectedCompositions.value.length) {
      const { valid, message } = validateCompositionsForSave(
        bulkCompositions,
        bulkCompositionType.value,
        bulkCompositionLocations.value
      );
      if (!valid) {
        options.error(message);
        return;
      }
    }
    const bulkValidationMessage = validateBulkOverridesBeforeSave();
    if (bulkValidationMessage) {
      options.error(bulkValidationMessage);
      return;
    }

    savingBulkComplete.value = true;
    bulkProgressLabel.value =
      options.selectedProductIds.value.length > SHOPIFY_BULK_BATCH_SIZE
        ? `Préparation (${options.selectedProductIds.value.length} fiches)…`
        : "";
    try {
      const overrides: PrestashopMelOverrides = {};
      if (bulkForm.brandId) {
        overrides.brandId = Number(bulkForm.brandId);
      }
      if (bulkForm.categoryId) {
        overrides.categoryId = Number(bulkForm.categoryId);
      }
      if (bulkForm.collectionId) {
        overrides.collectionId = Number(bulkForm.collectionId);
      }
      if (bulkForm.provenanceId) {
        overrides.provenanceId = Number(bulkForm.provenanceId);
      }
      const bulkPrice = parsePositivePrice(bulkForm.price);
      if (bulkPrice) {
        overrides.price = bulkPrice;
      }
      const bulkWeight = parsePositiveWeight(bulkForm.weight);
      if (bulkWeight) {
        overrides.weight = bulkWeight;
      }
      if (bulkCompositions.length) {
        overrides.compositionId = bulkCompositions[0]?.compositionId;
        overrides.compositionLocationId = bulkCompositions[0]?.locationId;
        overrides.compositionPercentage =
          Number(bulkCompositions[0]?.percentage) || undefined;
        overrides.compositions = bulkCompositions;
      }
      if (
        bulkForm.soldBy === "couleurs" ||
        bulkForm.soldBy === "melangees" ||
        bulkForm.soldBy === "tailles"
      ) {
        overrides.soldBy = bulkForm.soldBy;
      }
      if (bulkForm.packId) overrides.packId = Number(bulkForm.packId);
      if (bulkForm.sizeRangeId) {
        overrides.sizeRangeId = Number(bulkForm.sizeRangeId);
      }
      if (bulkSelectedColors.value.length) {
        overrides.colorId = bulkSelectedColors.value[0]?.id;
        overrides.colorIds = bulkSelectedColors.value.map((color) => color.id);
        overrides.colors = bulkSelectedColors.value.map((color) => ({
          colorId: color.id,
          isMain: color.isMain,
          quantitiesBySize: color.quantitesParTaille,
          stock:
            color.stock != null ? Math.max(0, Number(color.stock) || 0) : undefined,
        }));
      }

      const report = await saveShopifyBulkOverridesInBatches(
        options.selectedProductIds.value,
        overrides,
        {
          onBatchProgress: ({
            batchIndex,
            batchCount,
            updatedSoFar,
            failedSoFar,
          }) => {
            bulkProgressLabel.value =
              batchCount > 1
                ? `Lot ${batchIndex}/${batchCount} — ${updatedSoFar} mise(s) à jour, ${failedSoFar} échec(s)`
                : "";
          },
        }
      );

      if (
        report.updated > 0 &&
        bulkCompositionType.value === "autres" &&
        bulkSelectedCompositions.value.length
      ) {
        const byLocation = new Map<number, CompositionItem[]>();
        bulkSelectedCompositions.value.forEach((composition) => {
          const group = byLocation.get(composition.localisationId) || [];
          group.push(composition);
          byLocation.set(composition.localisationId, group);
        });
        byLocation.forEach((group) => rememberCompositionSet(group));
      }

      await options.onReloadCatalog();
      showBulkComplete.value = false;
      if (report.updated > 0) {
        options.success(
          `${report.updated} fiche(s) complétée(s)${
            report.failed ? `, ${report.failed} en échec` : ""
          }.`
        );
      }
      if (report.failed > 0) {
        const messages = report.results
          .filter((result) => !result.success)
          .map(
            (result) =>
              `${result.shopifyProductGid} : ${result.message || "échec"}`
          )
          .join("\n");
        options.error(messages);
      }
    } catch (caught) {
      options.error(
        caught instanceof Error ? caught.message : "Sauvegarde impossible."
      );
    } finally {
      savingBulkComplete.value = false;
      bulkProgressLabel.value = "";
    }
  }

  function onBulkCategoryPathSelect(selection: CategoryPathSelection) {
    bulkSelectedCategoryLevel1.value = selection.catId;
    bulkSelectedCategoryLevel2.value = selection.sousId;
    bulkSelectedCategoryLevel3.value = selection.sousSousId;
    bulkForm.categoryId = selection.leafId;
    configureBulkCompositionReferences(true);
  }

  function onBulkCategoryLevel1Change() {
    bulkSelectedCategoryLevel2.value = "";
    bulkSelectedCategoryLevel3.value = "";
    syncBulkCategoryIdFromLevels();
    configureBulkCompositionReferences(true);
  }

  function onBulkCategoryLevel2Change() {
    bulkSelectedCategoryLevel3.value = "";
    syncBulkCategoryIdFromLevels();
    configureBulkCompositionReferences(true);
  }

  function onBulkCategoryLevel3Change() {
    syncBulkCategoryIdFromLevels();
    configureBulkCompositionReferences(true);
  }

  function onBulkCategoryPathClear() {
    bulkSelectedCategoryLevel1.value = "";
    bulkSelectedCategoryLevel2.value = "";
    bulkSelectedCategoryLevel3.value = "";
    bulkForm.categoryId = "";
    configureBulkCompositionReferences(true);
  }

  async function handleBulkCompositionInput(
    value: string,
    localisationId: string | null
  ) {
    const term = value.trim();
    if (!term) {
      bulkCompositionAutocompleteOptions.value = [];
      bulkShowCompositionDropdown.value = false;
      return;
    }
    try {
      bulkIsLoadingCompositionAutocomplete.value = true;
      const selectedIds = bulkSelectedCompositions.value
        .filter((composition) =>
          localisationId
            ? composition.localisationId === Number(localisationId)
            : true
        )
        .map((composition) => composition.compositionId)
        .join("#");
      bulkCompositionAutocompleteOptions.value = await getCompositionAutocomplete(
        term,
        selectedIds
      );
      bulkShowCompositionDropdown.value =
        bulkCompositionAutocompleteOptions.value.length > 0;
    } catch {
      bulkCompositionAutocompleteOptions.value = [];
      bulkShowCompositionDropdown.value = false;
    } finally {
      bulkIsLoadingCompositionAutocomplete.value = false;
    }
  }

  function selectBulkComposition() {
    bulkCompositionAutocompleteOptions.value = [];
    bulkShowCompositionDropdown.value = false;
  }

  function hideBulkCompositionDropdown() {
    window.setTimeout(() => {
      bulkShowCompositionDropdown.value = false;
    }, 200);
  }

  watch(
    [() => bulkForm.soldBy, () => bulkForm.sizeRangeId],
    () => {
      if (bulkForm.soldBy === "tailles") {
        bulkForm.packId = "";
      } else if (
        bulkForm.packId &&
        !bulkPackOptions.value.some(
          (item) => String(item.value) === String(bulkForm.packId)
        )
      ) {
        bulkForm.packId = "";
      }
      syncBulkMelangeesColors();
    }
  );

  watch(
    () => bulkSelectedColors.value.map((color) => color.id).join(","),
    () => {
      syncBulkMelangeesColors();
    }
  );

  return {
    SHOPIFY_BULK_BATCH_SIZE,
    showBulkComplete,
    bulkModalLoading,
    savingBulkComplete,
    bulkProgressLabel,
    bulkSelectionProducts,
    bulkBatchCount,
    bulkMissingSummary,
    bulkForm,
    bulkSelectedCategoryLevel1,
    bulkSelectedCategoryLevel2,
    bulkSelectedCategoryLevel3,
    bulkSelectedCompositions,
    bulkCompositionType,
    bulkCompositionLocations,
    bulkShoeCompositions,
    bulkCompositionAutocompleteOptions,
    bulkShowCompositionDropdown,
    bulkIsLoadingCompositionAutocomplete,
    bulkSelectedColors,
    bulkFormValidationMessage,
    canSaveBulkForm,
    bulkMelangeesPackMismatchMessage,
    brandOptions,
    collectionOptions,
    provenanceOptions,
    sizeRangeOptions,
    categoryLevel1Options,
    bulkCategoryLevel2Options,
    bulkCategoryLevel3Options,
    bulkPackOptions,
    bulkSelectedSizeNames,
    soldByOptions,
    categoriesTree,
    officialColors,
    openBulkComplete,
    closeBulkComplete,
    saveBulkComplete,
    onBulkCategoryPathSelect,
    onBulkCategoryLevel1Change,
    onBulkCategoryLevel2Change,
    onBulkCategoryLevel3Change,
    onBulkCategoryPathClear,
    handleBulkCompositionInput,
    selectBulkComposition,
    hideBulkCompositionDropdown,
  };
}
